import { Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useState, useEffect, lazy, Suspense } from 'react';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import SEO from './components/seo/SEO';
import Home from './pages/Home';
import Loading from './components/error/Loading';
import ErrorBoundary from './components/error/ErrorBoundary';
import CookieConsent from './components/ui/CookieConsent';
import AnalyticsWrapper from './components/analytics/AnalyticsWrapper';
import ScrollToTop from './components/ui/ScrollToTop';
import analyticsService from './services/analyticsService';
import './App.css';

// Lazy load all routes except Home for faster initial load
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const ServicesLayout = lazy(() => import('./pages/services/ServicesLayout'));
const GCCService = lazy(() => import('./pages/services/GCCService'));
const HRService = lazy(() => import('./pages/services/HRService'));
const ProductEngineering = lazy(() => import('./pages/services/ProductEngineering'));
const InfrastructureSecurity = lazy(() => import('./pages/services/InfrastructureSecurity'));
const SpecialEngagements = lazy(() => import('./pages/services/SpecialEngagements'));
const AIInnovation = lazy(() => import('./pages/services/AIInnovation'));
const Products = lazy(() => import('./pages/Products'));
const SuccessStories = lazy(() => import('./pages/SuccessStories'));
const Careers = lazy(() => import('./pages/Careers'));
const Contact = lazy(() => import('./pages/Contact'));
const CaseStudies = lazy(() => import('./pages/CaseStudies'));
const CaseStudyDetail = lazy(() => import('./pages/CaseStudyDetail'));
const AnnualReturn = lazy(() => import('./pages/AnnualReturn'));

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);

  // Handle analytics consent
  const handleAnalyticsConsent = (hasConsent) => {
    setAnalyticsConsent(hasConsent);
    if (hasConsent) {
      analyticsService.initialize();
      analyticsService.setConsent(hasConsent);
    }
  };

  const closeMenu = () => setIsMenuOpen(false);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Cospin',
    url: 'https://www.cospin.in',
    logo: 'https://www.cospin.in/cospin-logo.png',
    description: 'Accelerating Digital Transformation with GCC setup, product engineering, and infrastructure/security solutions.',
    location: [
      {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Module 5, II Floor, Centre for Innovation Building, St.Joseph\'s College of Engineering, Semmencherry, OMR',
          addressLocality: 'Chennai',
          addressRegion: 'Tamil Nadu',
          postalCode: '600119',
          addressCountry: 'IN'
        }
      },
      {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '# 26, Laskar Hosur Road, Adugodi, Koramangala',
          addressLocality: 'Bengaluru',
          addressRegion: 'Karnataka',
          postalCode: '560030',
          addressCountry: 'IN'
        }
      }
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+91-98402-23344',
        contactType: 'customer service',
        areaServed: 'Chennai',
        email: 'admin@cospin.in'
      },
      {
        '@type': 'ContactPoint',
        telephone: '+91-98845-78933',
        contactType: 'customer service',
        areaServed: 'Bangalore',
        email: 'admin@cospin.in'
      }
    ],
    sameAs: [
      'https://www.linkedin.com/company/cospin',
      'https://x.com/cospin'
    ]
  };

  return (
    <ErrorBoundary>
      <div className="App">
        <ScrollToTop />
        <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
        <main className="main-content">
          <Suspense fallback={<Loading />}>
            <AnalyticsWrapper onLocationChange={() => setIsMenuOpen(false)}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<ServicesLayout />}>
                  <Route index element={<Services />} />
                  <Route path="gcc" element={<GCCService />} />
                  <Route path="hr-service" element={<HRService />} />
                  <Route path="product-engineering" element={<ProductEngineering />} />
                  <Route path="infrastructure-security" element={<InfrastructureSecurity />} />
                  <Route path="special-engagements" element={<SpecialEngagements />} />
                  <Route path="ai-innovation" element={<AIInnovation />} />
                </Route>
                <Route path="/products" element={<Products />} />
                <Route path="/success-stories" element={<SuccessStories />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/case-studies" element={<CaseStudies />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
                <Route path="/annualreturn" element={<AnnualReturn />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </AnalyticsWrapper>
          </Suspense>
        </main>
        <Footer />
        <CookieConsent onConsentChange={handleAnalyticsConsent} />
      </div>
    </ErrorBoundary>
  );
}

export default App;
